import React, { useState } from 'react';
import Router from 'next/router';
import { Box, Text, Heading } from 'mdlkit';
import { useIntl, FormattedMessage } from 'react-intl';
import base64 from 'base-64';
import { FocusButtonLink, FormSubmitButton } from 'components/shared/Styles';
import AccountRecoveryForm from '../components/login/AccountRecoveryForm';
import MultiplePhoneNumbersForm from '../components/login/MultiplePhoneNumbersForm';
import { AppCard, CallCustomerService } from '../components/shared';
import ContactSupportModal from '../components/login/ContactSupportModal';
import createAuthCodeAsync from '../api/login/createAuthCodeAsync';
import accountRecoveryAsync, {
  AccountRecoveryProps,
} from '../api/login/accountRecoveryAsync';
import multipleAccountsAsync from '../api/login/multipleAccountsAsync';
import {
  AuthCodeResponse,
  ValidateUser,
  MultiplePhoneNumbersUser,
} from '../interfaces/login';
import AuthCodeVerificationContainer from './login/AuthCodeVerificationContainer';
import AuthCodeVerificationWithoutSessionContainer from './login/AuthCodeVerificationWithoutSessionContainer';
import { ChooseAccount } from '../components/login';
import toast from '../utils/toast';
import { SMS, EMAIL } from '../constants/login';
import {
  INCORRECT_DATA,
  INVALID_PHONE,
  MULTIPLE_PHONE_NUMBERS,
  TOO_MANY_REQUESTS,
} from '../constants/errors';
import { formatDate } from '../utils/shared/normalize';
import { useCustomizable } from '../components/app/CustomizableProvider';
import { parseCustomParams } from '../utils/customParams';

const AccountRecoveryPage = () => {
  const { params: customParams } = useCustomizable();
  const [step, setStep] = useState<number>(1);
  const [
    showContactSupportModal,
    setShowContactSupportModal,
  ] = useState<boolean>(false);
  const [authCodeRes, setAuthCodeRes] = useState<AuthCodeResponse | null>(null);
  const [selectedUser, setSelectedUser] = useState<ValidateUser | null>(null);
  const [userList, setUserList] = useState<ValidateUser[] | null>(null);
  const [hasMultipleAcc, setHasMultipleAcc] = useState<boolean>(false);
  const [phoneNumbersUser, setPhoneNumbersUser] = useState<
    MultiplePhoneNumbersUser[] | null
  >(null);
  const [
    userPersonalData,
    setUserPersonalData,
  ] = useState<AccountRecoveryProps | null>(null);
  const { formatMessage: f } = useIntl();

  const isPhoneNumberNotNull = (phoneNumbers) => {
    return phoneNumbers.every(({ phone }) => phone !== null);
  };

  const sendAuthCode = async (token) => {
    const { authCode, error } = await createAuthCodeAsync({
      token,
      connection: SMS,
    });

    if (authCode) {
      setAuthCodeRes(authCode);
      setStep(2);
      window.createPageLoadEvent('/account-recovery#authenticate');
      return;
    }

    if (error?.code === MULTIPLE_PHONE_NUMBERS) {
      setPhoneNumbersUser(error.payload);
      if (isPhoneNumberNotNull(error.payload)) {
        setStep(3);
        window.createPageLoadEvent('/account-recovery#selectrecoveryphone');
      } else {
        toast.errorHtml(
          <CallCustomerService
            message={f({
              id: 'login.accountRecovery.failed.invalidPhoneError',
            })}
          />
        );
      }
      return;
    }

    let errorCause = '';
    if (error?.code === INVALID_PHONE) {
      errorCause = INVALID_PHONE;
      setStep(0);
      window.createPageLoadEvent('/account-recovery#accountnotfound');
    } else if (error?.code === TOO_MANY_REQUESTS) {
      errorCause = f({ id: 'login.accountRecovery.failed.tooManyRequests' });
      toast.error(f({ id: 'login.accountRecovery.failed.tooManyRequests' }));
    } else {
      errorCause = f({ id: 'error.unexpectedError' });
      toast.error(
        <CallCustomerService message={f({ id: 'error.unexpectedError' })} />
      );
    }

    window.createErrorEvent(
      {
        controlName: 'forgotusername-send-auth-code',
        controlText: 'send auth code',
      },
      {
        errorLocation: 'Login-Submit',
        errorType: 'Internal_API',
        errorCode: error?.code ?? '',
        errorCause,
      }
    );
  };

  const handleAccountRecovery = async (values) => {
    const { firstName, lastName, dobMonth, dobDay, dobYear } = values;
    const formattedValues = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      birthdate: formatDate(`${dobMonth}/${dobDay}/${dobYear}`, 'iso'),
    };
    setUserPersonalData(formattedValues);
    const { users, error } = await accountRecoveryAsync(formattedValues);

    if (users) {
      if (users.length > 1) {
        await sendAuthCode(users.map(({ token }) => token));
        setHasMultipleAcc(true); // handle multiple accounts with new token implementation
      } else {
        const [user] = users;
        await sendAuthCode(user.token);
        setSelectedUser(user);
      }

      window.createPageActionEvent(
        {
          controlName: 'forgotusername-success',
          controlText: 'Continue',
        },
        { userID: users[0].id.toString(), loggedInStatus: 'NOT_LOGGED_IN' }
      );
    } else {
      const errorData = {
        errorLocation: 'Login-Submit',
        errorType: 'Internal_API',
        errorCode: error?.code ?? '',
        errorCause: '',
      };

      if (error?.code === INCORRECT_DATA) {
        errorData.errorCause = 'Account not found';
        setStep(0);
        window.createPageLoadEvent('/account-recovery#accountnotfound');
      } else if (error?.code === TOO_MANY_REQUESTS) {
        errorData.errorCause = f({
          id: 'login.accountRecovery.failed.tooManyRequests',
        });
        toast.error(f({ id: 'login.accountRecovery.failed.tooManyRequests' }));
      } else {
        errorData.errorCause = f({ id: 'error.unexpectedError' });
        toast.error(
          <CallCustomerService message={f({ id: 'error.unexpectedError' })} />
        );
      }
      window.createErrorEvent(
        {
          controlName: 'forgotusername-attempt',
          controlText: 'Continue',
        },
        errorData
      );
    }
  };

  const redirectToPasswordField = (user: ValidateUser) => {
    const params = {
      id: user.id,
      username: user.username,
    };
    Router.push(
      parseCustomParams(
        `/sign-in?params=${base64.encode(JSON.stringify(params))}`,
        customParams
      )
    );
  };

  const handleAuthCodeSuccess = () => {
    if (selectedUser) redirectToPasswordField(selectedUser);
  };

  const handleAdobeError = (errorCause: string, errorCode?: string) => {
    window.createErrorEvent(
      {
        controlName: 'verify-youraccount-forgotusername-multiple',
        controlText: 'Verify',
      },
      {
        errorLocation: 'Login-Submit',
        errorType: 'Internal_API',
        errorCode: errorCode ?? '',
        errorCause,
      }
    );
  };

  const handleAuthCodeSuccessMultiple = async (userPhone) => {
    if (userPersonalData && userPhone) {
      const { users, error } = await multipleAccountsAsync({
        userIdentifier: userPhone,
        userPersonalData,
      });
      if (users) {
        if (users.length > 1) {
          setStep(4);
          setUserList(users);
          window.createPageLoadEvent('/account-recovery#multipleaccounts');
        } else {
          const [user] = users;
          redirectToPasswordField(user);
        }
        return;
      }
      handleAdobeError(error?.message.join(',') ?? '', error?.code);
      toast.errorHtml(error?.message.join(','));
      return;
    }

    handleAdobeError(f({ id: 'error.unexpectedError' }), 'unexpectedError');
    toast.error(
      <CallCustomerService message={f({ id: 'error.unexpectedError' })} />
    );
  };

  const handleSelectUser = (user: ValidateUser) => {
    redirectToPasswordField(user);
  };

  const handleSelectPhone = ({ token }) => {
    sendAuthCode(token);
  };

  return (
    <AppCard>
      {step === 1 && (
        <>
          <Heading align="center" as="h1" mb={30}>
            <FormattedMessage id="login.accountRecovery.headline" />
          </Heading>

          <Text>
            <FormattedMessage id="login.accountRecovery.subHeadline" />
          </Text>
          <AccountRecoveryForm onSubmit={handleAccountRecovery} />
        </>
      )}

      {hasMultipleAcc ? (
        <Box>
          {step === 2 && authCodeRes && (
            <AuthCodeVerificationWithoutSessionContainer
              headline={
                <Heading align="center" as="h1" mb={30}>
                  <FormattedMessage id="login.accountRecovery.signIn" />
                </Heading>
              }
              subHeadline={
                <Text>
                  <FormattedMessage
                    id="login.verifyAccountMessage.sms"
                    values={{
                      connectionValue: (
                        <strong>({authCodeRes.user.phone})</strong>
                      ),
                    }}
                  />
                </Text>
              }
              resendCodeCta={
                <Text>
                  <FormattedMessage id="login.resendCode" />
                </Text>
              }
              onResendConnection={SMS}
              authCodeRes={authCodeRes}
              onSuccess={handleAuthCodeSuccessMultiple}
              onResendCode={setAuthCodeRes}
              trackerResendId="resend-code-account-link"
              trackerVerifyId="verify-youraccount-forgotusername"
            />
          )}

          {step === 3 && phoneNumbersUser && (
            <>
              <Heading align="center" as="h1" mb={30}>
                <FormattedMessage id="login.accountRecovery.headline" />
              </Heading>

              <MultiplePhoneNumbersForm
                users={phoneNumbersUser}
                onSubmit={handleSelectPhone}
              />
            </>
          )}

          {step === 4 && userList && (
            <>
              <Heading align="center" as="h1" mb={30}>
                <FormattedMessage id="login.accountRecovery.signIn" />
              </Heading>

              <ChooseAccount
                userList={userList}
                onBack={() => setStep(1)}
                onSelectUser={handleSelectUser}
              />
            </>
          )}
        </Box>
      ) : (
        <Box>
          {step === 2 && authCodeRes && (
            <Box>
              <AuthCodeVerificationContainer
                headline={
                  <Heading align="center" as="h1" mb={30}>
                    <FormattedMessage id="login.accountRecovery.signIn" />
                  </Heading>
                }
                subHeadline={
                  <Text>
                    <FormattedMessage id="login.accountRecovery.accountBelongsYou" />{' '}
                    <FormattedMessage
                      id={`login.accountRecovery.${authCodeRes.connection}`}
                      values={{
                        connectionValue: (
                          <strong>
                            (
                            {authCodeRes.connection === EMAIL
                              ? authCodeRes.user.email
                              : authCodeRes.user.phone}
                            )
                          </strong>
                        ),
                      }}
                    />{' '}
                    <FormattedMessage id="login.accountRecovery.enterCodeBelow" />
                  </Text>
                }
                resendCodeCta={
                  <FormattedMessage id="passwordRecovery.resendOrTryAnotherWay" />
                }
                authCodeRes={authCodeRes}
                onSuccess={handleAuthCodeSuccess}
                onResendCode={setAuthCodeRes}
                trackId="verify-youraccount-forgotusername"
              />
            </Box>
          )}
        </Box>
      )}

      {step === 0 && (
        <Box textAlign="center">
          <Heading align="center" as="h1" mb={30}>
            <FormattedMessage id="login.accountRecovery.accountNotFound" />
          </Heading>
          <Text>
            <FormattedMessage id="login.accountRecovery.accountNotFoundDescription" />
          </Text>
          <FormSubmitButton
            my={30}
            size="large"
            onClick={() => setStep(1)}
            data-track-event
            data-track={JSON.stringify({
              controlName: 'try-again-button',
              controlText: f({ id: 'tryAgain' }),
            })}
          >
            <FormattedMessage id="tryAgain" />
          </FormSubmitButton>
          <Text>
            <FormattedMessage id="login.accountRecovery.failed.findAccount" />{' '}
            <FocusButtonLink
              onClick={() => setShowContactSupportModal(true)}
              data-track-event
              data-track={JSON.stringify({
                controlName: 'contact-forgotusername-support-link',
                controlText: f({ id: 'contactSupport' }),
              })}
            >
              <FormattedMessage id="contactSupport" />
            </FocusButtonLink>
          </Text>
          <ContactSupportModal
            isOpen={showContactSupportModal}
            onDismiss={() => setShowContactSupportModal(false)}
          />
        </Box>
      )}
    </AppCard>
  );
};

export default AccountRecoveryPage;
